import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { categories, siteInfo } from "@/mocks/categories";

const mainLinks = [
  { to: "/", label: "首页" },
  { to: "/rank", label: "热榜" },
  { to: "/about", label: "关于我们" },
  { to: "/contact", label: "联系我们" },
];

export default function SiteNavbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/rank?q=${encodeURIComponent(q)}` : "/rank");
    setSearchOpen(false);
    setMenuOpen(false);
  };

  const isActive = (to: string) => (to === "/" ? location.pathname === "/" : location.pathname.startsWith(to));

  return (
    <header className="sticky top-0 z-40 w-full border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="flex h-16 w-full items-center gap-4 px-4 md:px-8">
        <Link to="/" className="flex shrink-0 cursor-pointer items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
            <i className="ri-fire-fill text-lg" aria-hidden="true"></i>
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-lg font-bold tracking-tight text-foreground-950">{siteInfo.name}</span>
            <span className="hidden font-label text-[11px] text-foreground-500 sm:block">一眼看懂今天的热点</span>
          </span>
        </Link>

        <nav aria-label="主导航" className="ml-2 hidden items-center gap-1 md:flex">
          {mainLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`cursor-pointer whitespace-nowrap rounded-md px-3 py-2 font-label text-sm transition-colors ${
                isActive(link.to)
                  ? "bg-primary-100 font-medium text-primary-800"
                  : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          {searchOpen ? (
            <form onSubmit={handleSearch} className="hidden items-center md:flex" role="search">
              <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2">
                <i className="ri-search-line text-foreground-400" aria-hidden="true"></i>
                <input
                  type="search"
                  name="q"
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  placeholder="搜索话题关键词"
                  className="w-40 bg-transparent font-label text-sm text-foreground-900 outline-none placeholder:text-foreground-400 lg:w-52"
                  aria-label="搜索话题关键词"
                  autoFocus
                />
              </div>
              <button
                type="submit"
                className="ml-2 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-3 py-2 font-label text-sm text-background-50 transition-colors hover:bg-primary-600"
              >
                搜索
              </button>
            </form>
          ) : (
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              aria-label="打开搜索"
              className="hidden h-9 w-9 cursor-pointer items-center justify-center rounded-md text-foreground-600 transition-colors hover:bg-background-100 hover:text-foreground-950 md:flex"
            >
              <i className="ri-search-line text-lg" aria-hidden="true"></i>
            </button>
          )}

          <Link
            to="/contact"
            className="hidden cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 lg:inline-flex"
          >
            我要爆料
          </Link>

          <button
            type="button"
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-label={menuOpen ? "关闭菜单" : "打开菜单"}
            aria-expanded={menuOpen}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-foreground-700 transition-colors hover:bg-background-100 md:hidden"
          >
            <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} aria-hidden="true"></i>
          </button>
        </div>
      </div>

      <div className="hidden w-full border-t border-background-200 bg-background-100/60 md:block">
        <div className="flex items-center gap-1 px-4 py-2 md:px-8">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 font-label text-sm text-foreground-700 transition-colors hover:bg-background-50 hover:text-primary-700"
            >
              <i className={cat.icon} aria-hidden="true"></i>
              {cat.name}
            </Link>
          ))}
        </div>
      </div>

      {menuOpen && (
        <div className="fixed inset-x-0 top-16 bottom-0 z-50 overflow-y-auto border-t border-background-200 bg-background-50 px-4 py-5 md:hidden">
          <form onSubmit={handleSearch} className="mb-4" role="search">
            <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2.5">
              <i className="ri-search-line text-foreground-400" aria-hidden="true"></i>
              <input
                type="search"
                name="q"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索话题关键词"
                className="w-full bg-transparent font-label text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
                aria-label="搜索话题关键词"
              />
            </div>
          </form>

          <nav aria-label="移动端导航" className="flex flex-col gap-1">
            {mainLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`cursor-pointer rounded-md px-3 py-3 font-label text-sm transition-colors ${
                  isActive(link.to) ? "bg-primary-100 font-medium text-primary-800" : "text-foreground-800 hover:bg-background-100"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="mt-5 mb-2 font-label text-xs font-medium uppercase tracking-wide text-foreground-500">话题分类</div>
          <div className="grid grid-cols-2 gap-2">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="flex cursor-pointer items-center gap-2 rounded-md border border-background-200 px-3 py-2.5 font-label text-sm text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                <i className={cat.icon} aria-hidden="true"></i>
                {cat.name}
              </Link>
            ))}
          </div>

          <Link
            to="/contact"
            className="mt-6 block cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-3 text-center font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            我要爆料
          </Link>
        </div>
      )}
    </header>
  );
}